import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppShell } from 'shared/components/AppShell';
import { paths } from 'shared/routing/paths';
import { getExpertNav } from '../../navigation';
import { ExpertNdaBanner } from '../ExpertNdaBanner';
import { useExpertLayoutViewModel } from './viewModel';

/** Routes whose content is dense enough (tables, chat panes) to benefit from
 *  the full browser width instead of the shell's default centered column.
 *  `/expert/campaigns/` (trailing slash) only matches the campaign
 *  drill-down (`/expert/campaigns/:campaignId`, including its
 *  `/timesheet` sub-route) — not the campaigns list itself. */
const FULL_WIDTH_PATH_PREFIXES = ['/expert/campaigns/', '/expert/message-board'];
/** Exact-path full-width routes. */
const FULL_WIDTH_EXACT_PATHS = ['/expert/earnings'];
const TASK_WORKSPACE_PATH = '/expert/review-tasks';

function isRouteOrChild(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** Expert area shell: navy header + expert sidebar, with nested routes rendered
 *  into the content column. Everything else stays in the shell's default
 *  centered column. */
export function ExpertLayout() {
  const { pathname } = useLocation();
  const { assignedJobsCount, unreadMessagesCount } = useExpertLayoutViewModel();
  const taskWorkspace = isRouteOrChild(pathname, TASK_WORKSPACE_PATH);
  // The message board is an app-like screen. On phones the shell hands it the
  // exact viewport slot below the header with no gutters, and it pins its own
  // header and composer; from md up it is the usual padded, full-width page.
  const messageBoard = isRouteOrChild(pathname, paths.expert.messageBoard);
  const fullWidth =
    FULL_WIDTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    FULL_WIDTH_EXACT_PATHS.includes(pathname) ||
    taskWorkspace;
  return (
    <AppShell
      nav={getExpertNav({ assignedJobsCount, unreadMessagesCount })}
      hideSidebarOnMobile
      fullWidth={fullWidth}
      fullHeight={taskWorkspace}
      fullBleedOnMobile={messageBoard}
    >
      <ExpertNdaBanner className={messageBoard ? 'mx-4 mt-4 md:mx-0 md:mt-0' : undefined} />
      {taskWorkspace ? (
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      ) : messageBoard ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
      ) : (
        <Outlet />
      )}
    </AppShell>
  );
}
